import { Suspense } from "react";
import { lazyNamed } from "@/utils/staleChunk";
import { Navigate, Route, Routes } from "react-router";
import * as Sentry from "@sentry/react";
import { Button } from "antd";
import { PageNotFound } from "../components/PageNotFound";
import { PanelResult, PanelResultStatus } from "../components/PanelResult";
import { LoadingPage } from "@/components/LoadingPage";
import { MenuPageSkeleton } from "@/pages/clients/DynamicMenu/components/MenuContentSkeleton";

const DynamicMenu = lazyNamed(() => import("../pages/clients/DynamicMenu"), 'DynamicMenu');
const SessionPage = lazyNamed(() => import("../pages/backoffice/Auth"), 'SessionPage');
const DefineNewPassword = lazyNamed(() => import("@/pages/backoffice/Auth/components/DefineNewPassword"), 'DefineNewPassword');
const QRLinksPage = lazyNamed(() => import("../pages/public/QRLinks"), 'QRLinksPage');
const OrderStatusPage = lazyNamed(() => import("../pages/public/OrderStatus"), 'OrderStatusPage');
const JoinWaitlistPage = lazyNamed(() => import("../pages/public/Waitlist/JoinWaitlist"), 'JoinWaitlistPage');
const TrackWaitlistPage = lazyNamed(() => import("../pages/public/Waitlist/TrackWaitlist"), 'TrackWaitlistPage');
const CustomersPage = lazyNamed(() => import("../pages/public/Clients"), 'CustomersPage');
const DemoPage = lazyNamed(() => import("../pages/public/Demo"), 'DemoPage');
const TestMenus = lazyNamed(() => import("../pages/public/Demo/test-menus"), 'TestMenus');
const Rodeadouro = lazyNamed(() => import("../pages/clients/Rodeadouro"), 'Rodeadouro');

const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingPage message="Carregando..." />}>
    {children}
  </Suspense>
);

// A crash here is a stranger trying to order food, not staff — same top-level
// Sentry.ErrorBoundary (src/main.tsx) would otherwise catch it, but this gives
// them a message that matches the context instead of a generic app error.
const ClientMenuRoute = ({ children }: { children: React.ReactNode }) => (
  <Sentry.ErrorBoundary
    fallback={() => (
      <PanelResult
        status={PanelResultStatus.ERROR}
        title="Não foi possível abrir o cardápio"
        subtitle="Ocorreu um erro inesperado. Por favor, recarregue a página."
        extra={<Button type="primary" onClick={() => window.location.reload()}>Recarregar página</Button>}
      />
    )}
  >
    <Suspense fallback={<MenuPageSkeleton />}>{children}</Suspense>
  </Sentry.ErrorBoundary>
);

export const General = () => {
  const isDefaultPlatform = import.meta.env.VITE_APP_ENV_TYPE === "QRCODE_PREFERIDO";

  return (
    <Routes>
      <Route element={<LazyRoute><SessionPage /></LazyRoute>} path="/session" />
      <Route element={<Navigate to="/session#login" />} path="/login" />
      <Route element={<Navigate to="/session#signup" />} path="/signup" />
      <Route element={<Navigate to="/session#forgotpassword" />} path="/forgotpassword" />
      <Route element={<Navigate to="/session#login" />} path="/sysadmin" />
      <Route element={<Navigate to="/session#login" />} path="/sysadmin/home" />
      <Route element={<LazyRoute><DefineNewPassword /></LazyRoute>} path="/confirm-email" />
      <Route element={<LazyRoute><DefineNewPassword /></LazyRoute>} path="/reset-password" />
      <Route element={<ClientMenuRoute><DynamicMenu /></ClientMenuRoute>} path="/:menu" />
      <Route element={<LazyRoute><QRLinksPage /></LazyRoute>} path="/qrlinks/:slug" />
      <Route element={<LazyRoute><OrderStatusPage /></LazyRoute>} path="/order-status" />
      <Route element={<LazyRoute><JoinWaitlistPage /></LazyRoute>} path="/fila/:slug" />
      <Route element={<LazyRoute><TrackWaitlistPage /></LazyRoute>} path="/fila/:slug/:token" />
      <Route element={<PageNotFound />} path="/404" />

      {isDefaultPlatform && (
        <>
          <Route element={<LazyRoute><Rodeadouro /></LazyRoute>} path="/rodeadouro" />
          <Route element={<LazyRoute><DemoPage /></LazyRoute>} path="/demo/" />
          <Route element={<LazyRoute><CustomersPage /></LazyRoute>} path="/clientes/" />
          <Route element={<LazyRoute><TestMenus /></LazyRoute>} path="/all-menus/" />
        </>
      )}

      <Route element={<Navigate to="/clientes" />} path="/" />
    </Routes>
  );
};
